import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command";
import { Check, ChevronsUpDown } from "lucide-react";

export type Option = { id: number | string; name: string };

export type PairingFormState = {
  branch_id: string | number;
  username1: string | number; // LO user id
  username2: string | number; // CO user id
};

type Props = {
  form: PairingFormState;
  setForm: (cb: (prev: PairingFormState) => PairingFormState) => void;

  branches: Option[];
  users: Option[]; // users for selected branch

  loading?: boolean;
  isEdit?: boolean;
  onSubmit: () => void;
};

function clsx(...s: Array<string | false | undefined | null>) {
  return s.filter(Boolean).join(" ");
}

export function PairingForm({
  form,
  setForm,
  branches,
  users,
  loading,
  isEdit = false,
  onSubmit,
}: Props) {
  const dir = (document.documentElement.getAttribute("dir") as "ltr" | "rtl") || "ltr";
  const rtl = dir === "rtl";

  const [branchOpen, setBranchOpen] = useState(false);
  const [loOpen, setLoOpen] = useState(false);
  const [coOpen, setCoOpen] = useState(false);

  const selectedBranchName = useMemo(
    () => branches.find((b) => String(b.id) === String(form.branch_id))?.name,
    [branches, form.branch_id]
  );

  const selectedLoName = useMemo(
    () => users.find((u) => String(u.id) === String(form.username1))?.name,
    [users, form.username1]
  );

  const selectedCoName = useMemo(
    () => users.find((u) => String(u.id) === String(form.username2))?.name,
    [users, form.username2]
  );

  // when branch changes -> clear users selection
  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      username1: "",
      username2: "",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [String(form.branch_id)]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="space-y-4"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Branch */}
        <div className="space-y-1 md:col-span-2">
          <label className="text-xs text-muted-foreground">Branch</label>

          <Popover open={branchOpen} onOpenChange={setBranchOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" role="combobox" className="w-full justify-between rounded-xl">
                {form.branch_id ? (selectedBranchName || "Selected Branch") : "Select Branch"}
                <ChevronsUpDown className={clsx("h-4 w-4 opacity-50", rtl ? "mr-2" : "ml-2")} />
              </Button>
            </PopoverTrigger>

            <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
              <Command>
                <CommandInput placeholder="Search branch..." />
                <CommandEmpty>No branch found.</CommandEmpty>
                <CommandGroup>
                  {(branches ?? []).map((b) => (
                    <CommandItem
                      key={String(b.id)}
                      value={b.name}
                      onSelect={() => {
                        setForm((prev) => ({ ...prev, branch_id: String(b.id) }));
                        setBranchOpen(false);
                      }}
                    >
                      <Check
                        className={clsx(
                          "h-4 w-4",
                          rtl ? "ml-2" : "mr-2",
                          String(form.branch_id) === String(b.id) ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {b.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {/* Loan Officer */}
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Loan Officer Username</label>

          <Popover open={loOpen} onOpenChange={setLoOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                className="w-full justify-between rounded-xl"
                disabled={!form.branch_id}
              >
                {form.username1 ? (selectedLoName || "Selected LO") : "Select LO"}
                <ChevronsUpDown className={clsx("h-4 w-4 opacity-50", rtl ? "mr-2" : "ml-2")} />
              </Button>
            </PopoverTrigger>

            <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
              <Command>
                <CommandInput placeholder="Search user..." />
                <CommandEmpty>No user found.</CommandEmpty>
                <CommandGroup>
                  {(users ?? []).map((u) => (
                    <CommandItem
                      key={String(u.id)}
                      value={u.name}
                      onSelect={() => {
                        setForm((prev) => ({ ...prev, username1: String(u.id) }));
                        setLoOpen(false);
                      }}
                    >
                      <Check
                        className={clsx(
                          "h-4 w-4",
                          rtl ? "ml-2" : "mr-2",
                          String(form.username1) === String(u.id) ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {u.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {/* Collection Officer */}
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Collection Officer Username</label>

          <Popover open={coOpen} onOpenChange={setCoOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                className="w-full justify-between rounded-xl"
                disabled={!form.branch_id}
              >
                {form.username2 ? (selectedCoName || "Selected CO") : "Select CO"}
                <ChevronsUpDown className={clsx("h-4 w-4 opacity-50", rtl ? "mr-2" : "ml-2")} />
              </Button>
            </PopoverTrigger>

            <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
              <Command>
                <CommandInput placeholder="Search user..." />
                <CommandEmpty>No user found.</CommandEmpty>
                <CommandGroup>
                  {(users ?? []).map((u) => (
                    <CommandItem
                      key={String(u.id)}
                      value={u.name}
                      onSelect={() => {
                        setForm((prev) => ({ ...prev, username2: String(u.id) }));
                        setCoOpen(false);
                      }}
                    >
                      <Check
                        className={clsx(
                          "h-4 w-4",
                          rtl ? "ml-2" : "mr-2",
                          String(form.username2) === String(u.id) ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {u.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          type="submit"
          className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
          disabled={!!loading}
        >
          {loading ? "Saving..." : isEdit ? "Update" : "Create"}
        </Button>
      </div>
    </form>
  );
}